// src/pages/IADashboard.jsx
import React from "react";
import "./IADashboard.css";
import "./BaseLayout.css";

// HEADER
import DashboardHeader from "../components/iaDashboard/header/DashboardHeader";

// CARDS (Principais KPIs)
import ActiveSessionsCard from "../components/iaDashboard/cards/ActiveSessionsCard";
import PendingSessionsCard from "../components/iaDashboard/cards/PendingSessionsCard";
import FinishedTodayCard from "../components/iaDashboard/cards/FinishedTodayCard";
import AvgResponseTimeCard from "../components/iaDashboard/cards/AvgResponseTimeCard";
import AvgSessionTimeCard from "../components/iaDashboard/cards/AvgSessionTimeCard";

// NOVOS CARDS
import OperatorStatusCard from "../components/iaDashboard/cards/OperatorStatusCard";
import QueueSizeCard from "../components/iaDashboard/cards/QueueSizeCard";

// GRÁFICOS PRINCIPAIS
import SessionsPerDayChart from "../components/iaDashboard/charts/SessionsPerDayChart";
import PeakHoursChart from "../components/iaDashboard/charts/PeakHoursChart";
import AvatarDistributionChart from "../components/iaDashboard/charts/AvatarDistributionChart";
import AutoResolutionRateChart from "../components/iaDashboard/charts/AutoResolutionRateChart";
import SatisfactionChart from "../components/iaDashboard/charts/SatisfactionChart";

// NOVOS GRÁFICOS AVANÇADOS
import SLAChart from "../components/iaDashboard/charts/SLAChart";
import OperatorLoadChart from "../components/iaDashboard/charts/OperatorLoadChart";

// INSIGHTS
import EmotionAnalysis from "../components/iaDashboard/insights/EmotionAnalysis";
import KeywordsCloud from "../components/iaDashboard/insights/KeywordsCloud";
import RiskPatterns from "../components/iaDashboard/insights/RiskPatterns";
import TopicsTrend from "../components/iaDashboard/insights/TopicsTrend";

// REALTIME
import LiveStatsBar from "../components/iaDashboard/realtime/LiveStatsBar";
import RealtimeSessionsFeed from "../components/iaDashboard/realtime/RealtimeSessionsFeed";
import TypingIndicatorFeed from "../components/iaDashboard/realtime/TypingIndicatorFeed";

// TABELAS
import SessionsTable from "../components/iaDashboard/tables/SessionsTable";
import OperatorsTable from "../components/iaDashboard/tables/OperatorsTable";
import AtalhosUsageTable from "../components/iaDashboard/tables/AtalhosUsageTable";

// HOOKS
import useDashboardMetrics from "../hooks/iaDashboard/useDashboardMetrics";
import useRealtimeStats from "../hooks/iaDashboard/useRealtimeStats";
import useAdvancedMetrics from "../hooks/iaDashboard/useAdvancedMetrics";

export default function IADashboard() {
  const {
    loading,
    error,
    metrics = {},
    sessionsByDay = [],
    sessionsByHour = [],
    avatarDistribution = [],
    autoResolutionRate = 0,
  } = useDashboardMetrics();

  const { realtimeActiveCount = 0, realtimePendingCount = 0 } = useRealtimeStats();

  const {
    operatorStatus = { online: 0, busy: 0, offline: 0 },
    queueSize = 0,
    slaByOperator = [],
    operatorLoad = [],
    avgWaitTime = 0,
  } = useAdvancedMetrics();

  return (
    <div className="ia-dashboard-container">
      {/* CABEÇALHO */}
      <DashboardHeader
        activeCount={metrics.activeSessions || 0}
        pendingCount={metrics.pendingSessions || 0}
        finishedToday={metrics.finishedToday || 0}
      />

      {/* KPIs */}
      <section className="ia-dashboard-section ia-dashboard-section-kpis">
        <ActiveSessionsCard
          value={metrics.activeSessions || 0}
          realtimeValue={realtimeActiveCount}
        />
        <PendingSessionsCard
          value={metrics.pendingSessions || 0}
          realtimeValue={realtimePendingCount}
        />
        <FinishedTodayCard value={metrics.finishedToday || 0} />
        <AvgResponseTimeCard value={metrics.avgResponseTimeMinutes || 0} />
        <AvgSessionTimeCard value={metrics.avgSessionDurationMinutes || 0} />

        <OperatorStatusCard data={operatorStatus} />
        <QueueSizeCard value={queueSize} avgWait={avgWaitTime} />
      </section>

      {/* GRÁFICOS */}
      <section className="ia-dashboard-section ia-dashboard-section-charts">
        <div className="ia-dashboard-panel">
          <h3 className="ia-dashboard-panel-title">Atendimentos por dia</h3>
          <SessionsPerDayChart data={sessionsByDay} />
        </div>

        <div className="ia-dashboard-panel">
          <h3 className="ia-dashboard-panel-title">Picos de horário</h3>
          <PeakHoursChart data={sessionsByHour} />
        </div>

        <div className="ia-dashboard-panel">
          <h3 className="ia-dashboard-panel-title">Distribuição por avatar</h3>
          <AvatarDistributionChart data={avatarDistribution} />
        </div>

        <div className="ia-dashboard-panel">
          <h3 className="ia-dashboard-panel-title">Taxa de resolução automática</h3>
          <AutoResolutionRateChart rate={autoResolutionRate} />
        </div>

        <div className="ia-dashboard-panel">
          <h3 className="ia-dashboard-panel-title">Satisfação emocional</h3>
          <SatisfactionChart />
        </div>

        <div className="ia-dashboard-panel">
          <h3 className="ia-dashboard-panel-title">📊 SLA por operador</h3>
          <SLAChart data={slaByOperator} />
        </div>

        <div className="ia-dashboard-panel">
          <h3 className="ia-dashboard-panel-title">Carga de trabalho por operador</h3>
          <OperatorLoadChart data={operatorLoad} />
        </div>
      </section>

      {/* REALTIME */}
      <section className="ia-dashboard-section">
        <h2 className="ia-dashboard-block-title">📡 Realtime</h2>

        <div className="ia-dashboard-grid-3">
          <div className="ia-dashboard-panel"><LiveStatsBar /></div>
          <div className="ia-dashboard-panel"><RealtimeSessionsFeed /></div>
          <div className="ia-dashboard-panel"><TypingIndicatorFeed /></div>
        </div>
      </section>

      {/* INSIGHTS */}
      <section className="ia-dashboard-section">
        <h2 className="ia-dashboard-block-title">🧠 Insights da IA</h2>

        <div className="ia-dashboard-grid-4">
          <div className="ia-dashboard-panel"><EmotionAnalysis /></div>
          <div className="ia-dashboard-panel"><KeywordsCloud /></div>
          <div className="ia-dashboard-panel"><RiskPatterns /></div>
          <div className="ia-dashboard-panel"><TopicsTrend /></div>
        </div>
      </section>

      {/* TABELAS */}
      <section className="ia-dashboard-section">
        <h2 className="ia-dashboard-block-title">📊 Dados detalhados</h2>

        <div className="ia-dashboard-panel">
          <h3 className="ia-dashboard-panel-title">Sessões recentes</h3>
          <SessionsTable />
        </div>

        <div className="ia-dashboard-panel">
          <h3 className="ia-dashboard-panel-title">Operadores</h3>
          <OperatorsTable />
        </div>

        <div className="ia-dashboard-panel">
          <h3 className="ia-dashboard-panel-title">Uso de atalhos</h3>
          <AtalhosUsageTable />
        </div>
      </section>

      {/* STATUS */}
      {loading && (
        <div className="ia-dashboard-status-banner ia-dashboard-status-loading">
          Carregando dados da IA do Amor...
        </div>
      )}

      {error && (
        <div className="ia-dashboard-status-banner ia-dashboard-status-error">
          Ocorreu um erro ao carregar a dashboard. Verifique o console.
        </div>
      )}
    </div>
  );
}
